import { errorCodeOf, errorDetailsOf } from "@luckymenu/api-client";
import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import { useCallback, useState } from "react";
import { useStore } from "zustand";
import { useStaffCore } from "../provider";
import { useRestaurantId } from "./use-order";

/**
 * The API asked for a newer Android app (P6-T13): where to download it, or
 * null. `checkAgain` forgets the answer and refetches, for when the server's
 * minimum was lowered.
 */
export function useUpgradeRequired() {
	const core = useStaffCore();
	const upgrade = useStore(core.appGate, (s) => s.upgrade);
	const checkAgain = useCallback(() => {
		core.appGate.getState().clearUpgrade();
		void core.queryClient.refetchQueries();
	}, [core]);
	return { upgrade, checkAgain };
}

export type SwitchResult = "ok" | "wrong_pin" | "too_many" | "failed";

/** The member acting after a quick switch in the active restaurant, or null (P6-T11). */
export function useActiveSwitch() {
	const core = useStaffCore();
	const restaurantId = useRestaurantId();
	return useStore(core.staffSwitch, (s) =>
		s.active && s.active.restaurantId === restaurantId ? s.active : null,
	);
}

/**
 * More > Switch person (OP-19, P6-T11): who this device can switch to, the
 * member acting now, and the switch itself. Changes waiting in the outbox
 * are sent first, so they go under the person who made them. Switching to
 * the signed-in person ends the switch (with their PIN when they have one).
 */
export function useStaffSwitch() {
	const core = useStaffCore();
	const trpc = useTRPC();
	const restaurantId = useRestaurantId();
	const support = useStore(core.support, (s) => s.session !== null);
	const active = useActiveSwitch();
	const people = useQuery({
		...trpc.staff.tenancy.listSwitchable.queryOptions(),
		enabled: restaurantId !== null && !support,
	});
	const mutation = useMutation(trpc.staff.tenancy.switchPerson.mutationOptions());
	const mutateAsync = mutation.mutateAsync;

	const switchTo = useCallback(
		async (userId: string, pin?: string): Promise<SwitchResult> => {
			if (!restaurantId) return "failed";
			await core.outbox.flush().catch(() => {});
			try {
				const result = await mutateAsync({ userId, ...(pin ? { pin } : {}) });
				core.staffSwitch.getState().set(
					result.token && result.expiresAt
						? {
								restaurantId,
								token: result.token,
								userId: result.userId,
								name: result.name,
								role: result.role,
								expiresAt: result.expiresAt,
							}
						: null,
				);
				return "ok";
			} catch (error) {
				const code = errorCodeOf(error);
				if (code === "RATE_LIMITED") return "too_many";
				if (code === "FORBIDDEN" && errorDetailsOf(error).reason === "wrong_pin")
					return "wrong_pin";
				return "failed";
			}
		},
		[core, restaurantId, mutateAsync],
	);

	return {
		/** The member acting now after a switch, or null: the signed-in person. */
		active,
		people: people.data ?? [],
		loading: people.isPending && !support,
		working: mutation.isPending,
		switchTo,
	};
}

/** The acting person's own PIN in this restaurant: set, change, remove (P6-T11). */
export function useMyPin() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const restaurantId = useRestaurantId();
	const queryKey = trpc.staff.tenancy.getMyPin.queryKey();
	const query = useQuery({
		...trpc.staff.tenancy.getMyPin.queryOptions(),
		enabled: restaurantId !== null,
	});
	const onSuccess = (value: { hasPin: boolean }) => {
		queryClient.setQueryData(queryKey, value);
		void queryClient.invalidateQueries({ queryKey: trpc.staff.tenancy.listSwitchable.queryKey() });
	};
	const set = useMutation(trpc.staff.tenancy.setMyPin.mutationOptions({ onSuccess }));
	const remove = useMutation(trpc.staff.tenancy.removeMyPin.mutationOptions({ onSuccess }));
	const [saved, setSaved] = useState<"set" | "removed" | null>(null);
	return {
		hasPin: query.data?.hasPin ?? false,
		loading: query.isPending,
		saving: set.isPending || remove.isPending,
		failed: set.isError || remove.isError,
		saved,
		async setPin(pin: string) {
			setSaved(null);
			await set.mutateAsync({ pin });
			setSaved("set");
		},
		async removePin() {
			setSaved(null);
			await remove.mutateAsync();
			setSaved("removed");
		},
	};
}
